import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import { logError } from "@/lib/logger";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  HelpCircle,
  Trash2,
  Plus,
  Edit,
} from "lucide-react";

export const FAQManager = () => {
  const { token } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ question: "", answer: "", category: "general" });

  const fetchFAQs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/faq`);
      setFaqs(response.data);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFAQs();
  }, [fetchFAQs]);

  const saveFAQ = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/faq/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("FAQ updated!");
      } else {
        await axios.post(`${API}/admin/faq`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("FAQ added!");
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ question: "", answer: "", category: "general" });
      fetchFAQs();
    } catch (error) {
      toast.error("Failed to save FAQ");
    }
  };

  const editFAQ = (faq) => {
    setFormData({ question: faq.question, answer: faq.answer, category: faq.category });
    setEditingId(faq.id);
    setShowForm(true);
  };

  const deleteFAQ = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await axios.delete(`${API}/admin/faq/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("FAQ deleted");
      fetchFAQs();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-faq-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal">FAQ Manager</h1>
          <p className="font-body text-stone-grey">Manage your frequently asked questions</p>
        </div>
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); setFormData({ question: "", answer: "", category: "general" }); }} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      {showForm && (
        <div className="glass rounded-xl p-6 mb-8">
          <form onSubmit={saveFAQ} className="space-y-4">
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Question</Label>
              <Input 
                value={formData.question} 
                onChange={(e) => setFormData({...formData, question: e.target.value})}
                placeholder="What is your cancellation policy?"
                required
                className="form-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Answer</Label>
              <Textarea 
                value={formData.answer} 
                onChange={(e) => setFormData({...formData, answer: e.target.value})}
                placeholder="I require 24 hours notice for cancellations..."
                required
                rows={4}
                className="form-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Category</Label>
              <select 
                value={formData.category} 
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="form-input w-full"
              >
                <option value="general">General</option>
                <option value="booking">Booking</option>
                <option value="donations">Donations</option>
                <option value="travel">Travel</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="btn-primary">{editingId ? "Update" : "Add"} FAQ</Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : faqs.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <HelpCircle className="w-12 h-12 text-taupe mx-auto mb-4" />
          <p className="font-body text-stone-grey">No FAQs yet. Add your first question!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div key={faq.id} className="glass rounded-xl p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <span className="text-xs bg-jade-soft/30 text-jade-text px-2 py-1 rounded uppercase">{faq.category}</span>
                  <h3 className="font-heading text-lg text-charcoal mt-2">{faq.question}</h3>
                  <p className="font-body text-stone-grey mt-2">{faq.answer}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => editFAQ(faq)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteFAQ(faq.id)}>
                    <Trash2 className="w-4 h-4 text-lipstick-red" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

